import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Popover, Portal, Transition } from '@headlessui/react';
import {
    BellIcon,
    ChatBubbleLeftRightIcon,
    CheckCircleIcon,
    ChevronRightIcon,
    MegaphoneIcon,
    VideoCameraIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import {
    fetchNotifications,
    fetchMoreNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
} from '../../redux/actions/notifications';
import { setNotificationsFilter } from '../../redux/reducers/notifications';
import { NOTIFICATION_FILTERS } from '../../common/notificationTypes';

const PANEL_Z_INDEX = 2000;
const PANEL_GAP = 8;
const MOBILE_BREAKPOINT = 640;

const absoluteTime = (date) => {
    if (!date) return '';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' });
};

const relativeTime = (date) => {
    if (!date) return '';
    const diff = Date.now() - new Date(date).getTime();
    if (Number.isNaN(diff)) return '';

    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `${minutes} min`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;

    const days = Math.floor(hours / 24);
    if (days === 1) return 'Ayer';
    if (days < 7) return `${days}d`;

    return new Date(date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
};

const splitNotificationBody = (body = '') => {
    const separator = String(body).indexOf(': ');
    if (separator === -1) return { sender: '', text: String(body) };
    return {
        sender: String(body).slice(0, separator),
        text: String(body).slice(separator + 2),
    };
};

const usePanelAnchor = (open, anchorRef) => {
    const [anchor, setAnchor] = useState(null);

    useEffect(() => {
        if (!open) return undefined;

        const measure = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;

            const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
            const top = Math.round(rect.bottom + PANEL_GAP);
            const available = window.innerHeight - top - 24;

            setAnchor({
                top,
                isMobile,
                right: isMobile ? 8 : Math.round(Math.max(8, window.innerWidth - rect.right)),
                left: isMobile ? 8 : undefined,
                width: isMobile ? 'calc(100vw - 16px)' : '400px',
                listMaxHeight: Math.round(Math.max(280, Math.min(480, available - 140))),
            });
        };

        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);

        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [open, anchorRef]);

    return anchor;
};

const NOTIFICATION_ICONS = {
    class_live: { Icon: VideoCameraIcon, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    teacher_notice: { Icon: MegaphoneIcon, color: 'text-violet-600', bg: 'bg-violet-50' },
    default: { Icon: ChatBubbleLeftRightIcon, color: 'text-sky-600', bg: 'bg-sky-50' },
};

const NotificationRow = ({ notification, onOpen }) => {
    const { sender, text } = splitNotificationBody(notification.body);
    const isUnread = !notification.read;
    const iconStyle = NOTIFICATION_ICONS[notification.type] || NOTIFICATION_ICONS.default;
    const { Icon, color, bg } = iconStyle;

    return (
        <li>
            <button
                type="button"
                onClick={onOpen}
                className={[
                    'group relative flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors duration-150',
                    'focus:outline-none focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500',
                    'hover:bg-slate-50',
                    isUnread ? 'bg-slate-50/70' : '',
                ].join(' ')}
            >
                {isUnread ? (
                    <span
                        aria-hidden="true"
                        className="absolute left-1.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-indigo-500"
                    />
                ) : null}

                <span className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${bg}`}>
                    <Icon className={`h-[18px] w-[18px] ${color}`} aria-hidden="true" />
                </span>

                <span className="min-w-0 flex-1 pr-6">
                    <span className="flex items-baseline justify-between gap-2">
                        <span
                            className={`line-clamp-1 text-[13px] leading-5 ${
                                isUnread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'
                            }`}
                        >
                            {notification.title}
                        </span>
                        <time
                            dateTime={notification.createdAt}
                            title={absoluteTime(notification.createdAt)}
                            className="flex-shrink-0 text-[11px] tabular-nums text-slate-400 transition-opacity duration-150 group-hover:opacity-0"
                        >
                            {relativeTime(notification.createdAt)}
                        </time>
                    </span>

                    <span className="mt-0.5 line-clamp-2 text-xs leading-5 text-slate-500" title={notification.body}>
                        {sender ? <span className="font-medium text-slate-600">{sender}: </span> : null}
                        {text}
                    </span>
                </span>

                <span
                    aria-hidden="true"
                    className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 opacity-0 transition-all duration-150 group-hover:translate-x-0.5 group-hover:opacity-100 group-focus-visible:opacity-100"
                >
                    <ChevronRightIcon className="h-4 w-4" strokeWidth={2.5} />
                </span>
            </button>
        </li>
    );
};

const RowsSkeleton = () => (
    <ul aria-hidden="true">
        {[0, 1, 2, 3].map((row) => (
            <li key={row} className="flex items-start gap-3 px-4 py-3.5">
                <span className="h-9 w-9 flex-shrink-0 animate-pulse rounded-xl bg-slate-100" />
                <span className="min-w-0 flex-1 space-y-2 py-0.5">
                    <span className="block h-3.5 w-2/3 animate-pulse rounded bg-slate-100" />
                    <span className="block h-3 w-4/5 animate-pulse rounded bg-slate-50" />
                </span>
            </li>
        ))}
    </ul>
);

const FilterTabs = ({ filter, unreadCount, onChange }) => {
    const tabs = [
        { id: NOTIFICATION_FILTERS.unread, label: 'Sin leer', count: unreadCount },
        { id: NOTIFICATION_FILTERS.read, label: 'Leídas' },
    ];

    return (
        <div
            role="tablist"
            aria-label="Filtrar notificaciones"
            className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1"
        >
            {tabs.map((tab) => {
                const selected = filter === tab.id;
                return (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        id={`notifications-tab-${tab.id}`}
                        aria-selected={selected}
                        aria-controls="notifications-tabpanel"
                        onClick={() => onChange(tab.id)}
                        className={[
                            'relative flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-all duration-150',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1',
                            selected
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700',
                        ].join(' ')}
                    >
                        {tab.label}
                        {typeof tab.count === 'number' && tab.count > 0 ? (
                            <span
                                className={[
                                    'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums',
                                    selected
                                        ? 'bg-indigo-100 text-indigo-700'
                                        : 'bg-slate-200/80 text-slate-600',
                                ].join(' ')}
                            >
                                {tab.count > 99 ? '99+' : tab.count}
                            </span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
};

const EmptyState = ({ isUnreadTab, onShowRead }) => (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
        <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <BellIcon className="h-6 w-6 text-slate-400" aria-hidden="true" />
        </span>
        <p className="text-sm font-medium text-slate-700">
            {isUnreadTab ? 'Sin notificaciones nuevas' : 'Sin historial'}
        </p>
        <p className="mt-1 max-w-[210px] text-xs leading-5 text-slate-500">
            {isUnreadTab
                ? 'Cuando lleguen mensajes o avisos aparecerán aquí'
                : 'Las notificaciones leídas se guardan aquí'}
        </p>
        {isUnreadTab ? (
            <button
                type="button"
                onClick={onShowRead}
                className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
            >
                Ver historial
            </button>
        ) : null}
    </div>
);

const NotificationsPanel = ({ open, anchorRef }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { items, unreadCount, loading, loadingMore, hasMore, filter } = useSelector(
        (state) => state.notifications,
    );

    const listRef = useRef(null);
    const sentinelRef = useRef(null);
    const anchor = usePanelAnchor(open, anchorRef);

    const notifications = items || [];
    const isUnreadTab = filter === NOTIFICATION_FILTERS.unread;
    const isEmpty = !loading && notifications.length === 0;

    useEffect(() => {
        if (!open) return;
        dispatch(fetchNotifications({ filter })).catch(() => {});
    }, [open, filter, dispatch]);

    const selectFilter = useCallback(
        (nextFilter) => {
            if (nextFilter === filter) return;
            dispatch(setNotificationsFilter(nextFilter));
        },
        [dispatch, filter],
    );

    const loadMore = useCallback(() => {
        dispatch(fetchMoreNotifications()).catch(() => {});
    }, [dispatch]);

    useEffect(() => {
        if (!open || !hasMore) return undefined;

        const sentinel = sentinelRef.current;
        const root = listRef.current;
        if (!sentinel || !root) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) loadMore();
            },
            { root, rootMargin: '64px' },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [open, hasMore, notifications.length, filter, loadMore]);

    const handleOpenNotification = (notification, close) => {
        if (!notification.read) {
            dispatch(markNotificationAsRead(notification._id)).catch(() => {});
        }

        const courseId = notification.data?.courseId;
        if (courseId) {
            const tab =
                notification.type === 'teacher_notice'
                    ? '?tab=notices'
                    : notification.type === 'class_live'
                      ? '?tab=classes'
                      : '';
            navigate(`/curso/${courseId}${tab}`);
        }
        close();
    };

    const handleMarkAllRead = () => {
        dispatch(markAllNotificationsAsRead()).catch(() => {});
    };

    const panelStyle = {
        zIndex: PANEL_Z_INDEX,
        top: anchor?.top ?? 80,
        width: anchor?.width ?? '400px',
        ...(anchor?.isMobile
            ? { left: anchor.left, right: anchor.right }
            : { right: anchor?.right ?? 16 }),
    };

    return (
        <Portal>
            <Transition
                as={Fragment}
                show={open}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0 scale-[0.98] -translate-y-1"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-[0.98]"
            >
                <Popover.Panel
                    static
                    aria-label="Centro de notificaciones"
                    style={panelStyle}
                    className="fixed overflow-hidden rounded-2xl bg-white shadow-xl shadow-slate-200/60 ring-1 ring-slate-200/70 focus:outline-none"
                >
                    {({ close }) => (
                        <>
                            {/* Header: mismo acomodo, fondo blanco */}
                            <div className="border-b border-slate-100 px-4 pb-3 pt-4">
                                <div className="mb-3 flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                                            <BellIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                        <div>
                                            <h2 className="text-sm font-semibold text-slate-900">
                                                Notificaciones
                                            </h2>
                                            {unreadCount > 0 ? (
                                                <p className="text-xs text-slate-500">
                                                    {unreadCount} {unreadCount === 1 ? 'nueva' : 'nuevas'}
                                                </p>
                                            ) : (
                                                <p className="text-xs text-slate-400">Al día</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => close()}
                                        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                        aria-label="Cerrar"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <FilterTabs
                                    filter={filter}
                                    unreadCount={unreadCount}
                                    onChange={selectFilter}
                                />
                            </div>

                            {isUnreadTab && unreadCount > 0 ? (
                                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                                    <span className="text-xs font-medium text-slate-500">
                                        {unreadCount} sin leer
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleMarkAllRead}
                                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 transition-colors hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                    >
                                        <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                        Marcar todas
                                    </button>
                                </div>
                            ) : null}

                            <div
                                id="notifications-tabpanel"
                                role="tabpanel"
                                aria-labelledby={`notifications-tab-${filter}`}
                                ref={listRef}
                                style={{ maxHeight: anchor?.listMaxHeight ?? 360 }}
                                className="overflow-y-auto overscroll-contain"
                            >
                                {loading && notifications.length === 0 ? <RowsSkeleton /> : null}

                                {isEmpty ? (
                                    <EmptyState
                                        isUnreadTab={isUnreadTab}
                                        onShowRead={() => selectFilter(NOTIFICATION_FILTERS.read)}
                                    />
                                ) : null}

                                {notifications.length > 0 ? (
                                    <ul className="divide-y divide-slate-100">
                                        {notifications.map((notification) => (
                                            <NotificationRow
                                                key={notification._id}
                                                notification={notification}
                                                onOpen={() => handleOpenNotification(notification, close)}
                                            />
                                        ))}
                                    </ul>
                                ) : null}

                                {notifications.length > 0 && hasMore ? (
                                    <div
                                        ref={sentinelRef}
                                        className="flex items-center justify-center py-3"
                                        aria-live="polite"
                                        aria-busy={loadingMore}
                                    >
                                        {loadingMore ? (
                                            <p className="text-center text-xs text-slate-400">Cargando...</p>
                                        ) : (
                                            <span className="h-1 w-1" aria-hidden="true" />
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </>
                    )}
                </Popover.Panel>
            </Transition>
        </Portal>
    );
};

export const ButtonNotifications = () => {
    const unreadCount = useSelector((state) => state.notifications.unreadCount);
    const buttonRef = useRef(null);
    const hasUnread = unreadCount > 0;

    return (
        <Popover className="relative">
            {({ open }) => (
                <>
                    <Popover.Button
                        ref={buttonRef}
                        className={[
                            'relative flex h-10 w-10 items-center justify-center rounded-full transition-colors',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                            open
                                ? 'bg-indigo-50 text-indigo-600'
                                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
                        ].join(' ')}
                    >
                        <span className="sr-only">
                            {hasUnread ? `Notificaciones, ${unreadCount} sin leer` : 'Notificaciones'}
                        </span>
                        <BellIcon className="h-5 w-5" aria-hidden="true" />

                        {hasUnread ? (
                            <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white">
                                {unreadCount > 99 ? '99' : unreadCount}
                            </span>
                        ) : null}
                    </Popover.Button>

                    <NotificationsPanel open={open} anchorRef={buttonRef} />
                </>
            )}
        </Popover>
    );
};
